import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft,
  RefreshCw,
  Sparkles,
  Star,
  Star as StarIcon,
  Trash2,
  ExternalLink,
} from "lucide-react";

import { AdminGate } from "@/components/admin/AdminGate";
import { Button } from "@/components/ui/button";
import {
  listarDepoimentosAdmin,
  rodarSyncGoogleReviews,
  rodarCuradoriaGoogleReviews,
  alternarDestaque,
  removerDaCuradoria,
} from "@/lib/depoimentos/admin.functions";

export const Route = createFileRoute("/admin/depoimentos")({
  head: () => ({
    meta: [
      { title: "Admin — Depoimentos" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => (
    <AdminGate route="/admin/depoimentos">
      <DepoimentosAdminPage />
    </AdminGate>
  ),
});

function DepoimentosAdminPage() {
  const qc = useQueryClient();
  const listar = useServerFn(listarDepoimentosAdmin);
  const sync = useServerFn(rodarSyncGoogleReviews);
  const curar = useServerFn(rodarCuradoriaGoogleReviews);
  const toggle = useServerFn(alternarDestaque);
  const remover = useServerFn(removerDaCuradoria);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-depoimentos"],
    queryFn: () => listar(),
  });

  const [busy, setBusy] = useState<"sync" | "curate" | null>(null);

  const mToggle = useMutation({
    mutationFn: (v: { id: string; destaque: boolean }) => toggle({ data: v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-depoimentos"] }),
  });
  const mRemove = useMutation({
    mutationFn: (id: string) => remover({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-depoimentos"] }),
  });

  async function handleSync() {
    setBusy("sync");
    try {
      const r = await sync();
      if (!r.ok) toast.error(`Sync falhou: ${r.error ?? "erro desconhecido"}`);
      else toast.success(`Sync OK · ${r.upserted}/${r.fetched} reviews salvas`);
      qc.invalidateQueries({ queryKey: ["admin-depoimentos"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function handleCurate() {
    setBusy("curate");
    try {
      const r = await curar();
      if (!r.ok) toast.error(`Curadoria falhou: ${r.error ?? "erro desconhecido"}`);
      else
        toast.success(
          `Curadoria OK · ${r.processed} novos · ${r.curated_total} depoimentos publicados`,
        );
      qc.invalidateQueries({ queryKey: ["admin-depoimentos"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="min-h-screen">
      <div className="mx-auto max-w-6xl px-4 py-10 md:py-14 space-y-8">
        <Link
          to="/admin"
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> voltar ao centro de controle
        </Link>

        <header className="space-y-2">
          <h1 className="text-3xl md:text-4xl font-bold">Depoimentos (Google Business)</h1>
          <p className="text-muted-foreground max-w-2xl">
            Sincronização com o perfil do Google e curadoria automática por IA. A página pública
            fica em{" "}
            <Link to="/depoimentos" className="underline underline-offset-2">
              /depoimentos
            </Link>{" "}
            <ExternalLink className="inline h-3 w-3" />
          </p>
        </header>

        <section className="grid md:grid-cols-3 gap-4">
          <div className="rounded-xl border bg-card p-5">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              Reviews (raw)
            </div>
            <div className="mt-2 text-3xl font-bold">{data?.raw_total ?? "—"}</div>
            <div className="text-xs text-muted-foreground mt-1">
              Total sincronizado do Google
            </div>
          </div>
          <div className="rounded-xl border bg-card p-5">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              Curados (públicos)
            </div>
            <div className="mt-2 text-3xl font-bold">{data?.curated_total ?? "—"}</div>
            <div className="text-xs text-muted-foreground mt-1">Máx. 300 no ar</div>
          </div>
          <div className="rounded-xl border bg-card p-5">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Ações</div>
            <div className="mt-2 flex flex-col gap-2">
              <Button
                onClick={handleSync}
                disabled={busy !== null}
                size="sm"
                variant="outline"
                className="justify-start"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${busy === "sync" ? "animate-spin" : ""}`} />
                {busy === "sync" ? "Sincronizando…" : "Sincronizar do Google"}
              </Button>
              <Button
                onClick={handleCurate}
                disabled={busy !== null}
                size="sm"
                className="justify-start"
              >
                <Sparkles className={`h-4 w-4 mr-2 ${busy === "curate" ? "animate-pulse" : ""}`} />
                {busy === "curate" ? "Curando com IA…" : "Rodar curadoria (IA)"}
              </Button>
            </div>
          </div>
        </section>

        <section className="rounded-xl border bg-card">
          <div className="p-5 border-b">
            <h2 className="font-semibold">Últimas operações</h2>
          </div>
          <ul className="divide-y">
            {(data?.log ?? []).length === 0 ? (
              <li className="p-5 text-sm text-muted-foreground">Nenhum sync registrado ainda.</li>
            ) : (
              (data?.log ?? []).map((l, i) => (
                <li key={i} className="p-4 text-sm flex items-start justify-between gap-4">
                  <div>
                    <div className="font-medium">{l.title}</div>
                    <div className="text-xs text-muted-foreground">{l.description}</div>
                  </div>
                  <div className="text-xs text-muted-foreground shrink-0">
                    {new Date(l.created_at).toLocaleString("pt-BR")}
                  </div>
                </li>
              ))
            )}
          </ul>
        </section>

        <section className="rounded-xl border bg-card">
          <div className="p-5 border-b flex items-center justify-between">
            <h2 className="font-semibold">Depoimentos curados</h2>
            <span className="text-xs text-muted-foreground">
              Ordenados por destaque → score IA → recentes
            </span>
          </div>
          {isLoading ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Carregando…</div>
          ) : (data?.curated ?? []).length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              Ainda não há depoimentos curados. Rode <b>Sincronizar</b> e depois <b>Curadoria</b>.
            </div>
          ) : (
            <ul className="divide-y max-h-[70vh] overflow-y-auto">
              {(data?.curated ?? []).map((c) => (
                <li key={c.id} className="p-4 flex flex-col md:flex-row md:items-start gap-3">
                  <div className="md:w-56 shrink-0 space-y-1">
                    <div className="font-semibold text-sm">{c.author_name}</div>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star
                          key={i}
                          className={
                            i < c.rating
                              ? "h-3.5 w-3.5 fill-amber-400 text-admin-accent"
                              : "h-3.5 w-3.5 fill-slate-200 text-slate-200"
                          }
                        />
                      ))}
                      <span className="text-xs text-muted-foreground ml-1">
                        score {c.score_ia}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {(c.tema ?? []).map((t) => (
                        <span
                          key={t}
                          className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-600"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground flex-1 whitespace-pre-line">
                    {c.comment}
                  </p>
                  <div className="flex flex-row md:flex-col gap-2 shrink-0">
                    <Button
                      variant={c.destaque ? "default" : "outline"}
                      size="sm"
                      onClick={() =>
                        mToggle.mutate({ id: c.id, destaque: !c.destaque })
                      }
                      title={c.destaque ? "Remover destaque" : "Fixar em destaque"}
                    >
                      <StarIcon
                        className={`h-3.5 w-3.5 ${c.destaque ? "fill-current" : ""}`}
                      />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (confirm("Remover este depoimento da página pública?")) {
                          mRemove.mutate(c.id);
                        }
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="rounded-xl border bg-muted/30 p-5 text-xs text-muted-foreground space-y-2">
          <p className="font-semibold text-foreground">Como funciona</p>
          <ol className="list-decimal ml-5 space-y-1">
            <li>
              <b>Sincronizar</b>: puxa todas as reviews do perfil Google Business Profile
              (v4/reviews) para <code>google_reviews</code>. Upsert por <code>google_review_id</code>.
            </li>
            <li>
              <b>Curadoria (IA)</b>: filtra reviews 4–5★ com comentário ≥ 80 caracteres, pontua
              cada um de 0 a 100 e extrai 2–4 tags (tema). Mantém os TOP 300 na tabela pública{" "}
              <code>google_reviews_curated</code>.
            </li>
            <li>
              <b>Página pública</b>: <Link to="/depoimentos" className="underline">/depoimentos</Link> com JSON-LD de{" "}
              <code>AggregateRating</code> + <code>Review</code> — melhora GEO/SEO nas buscas de LLMs
              e no Google.
            </li>
            <li>
              <b>Automação</b>: endpoint <code>/api/public/hooks/sync-google-reviews</code>{" "}
              (header <code>x-cron-secret</code>) pode ser agendado semanalmente via pg_cron.
            </li>
          </ol>
        </section>
      </div>
    </div>
  );
}
